/**
 * Shared chat utilities used by both GaiaChatWidget and RaiChatPage.
 *
 * Extracted from GaiaChatWidget.jsx to avoid duplication.
 */
import DOMPurify from 'dompurify';

// ---------------------------------------------------------------------------
// Markdown helpers
// ---------------------------------------------------------------------------

/**
 * Strip GAIA RAG citation links from the response text.
 */
export function stripCitations(text) {
  let cleaned = text.replace(/\[\[\d+\]\]\([^)]+\)/g, '');
  cleaned = cleaned.replace(
    /(\n*-{0,}\n*)(This response has been generated by AI[\s\S]*$)/,
    '\n\n<div class="gaia-ai-disclaimer">Disclaimer: $2</div>',
  );
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim();
}

/**
 * Lightweight markdown-to-HTML converter for GAIA responses.
 */
export function renderMarkdown(md) {
  let html = md;

  // Fenced code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<pre><code class="language-${lang}">${escaped}</code></pre>`;
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Headings
  html = html.replace(/^#### (.+)$/gm, '<h6>$1</h6>');
  html = html.replace(/^### (.+)$/gm, '<h5>$1</h5>');
  html = html.replace(/^## (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^# (.+)$/gm, '<h3>$1</h3>');

  // Horizontal rules
  html = html.replace(/^-{3,}$/gm, '<hr/>');

  // Bold + italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Links
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener">$1 <i class="fas fa-external-link-alt gaia-link-icon"></i></a>',
  );

  // Tables
  html = html.replace(/(?:^\|.+\|[ ]*$\n?)+/gm, match => {
    const rows = match
      .trim()
      .split('\n')
      .filter(r => r.trim());
    if (rows.length < 2) return match;
    const parseRow = r =>
      r
        .split('|')
        .slice(1, -1)
        .map(c => c.trim());
    const headerCells = parseRow(rows[0]);
    const isSep = /^\|[\s:?-]+\|/.test(rows[1]);
    const dataRows = rows.slice(isSep ? 2 : 1);
    let table = '<div class="gaia-table-wrap"><table class="gaia-table"><thead><tr>';
    headerCells.forEach(c => {
      table += `<th>${c}</th>`;
    });
    table += '</tr></thead><tbody>';
    dataRows.forEach(row => {
      const cells = parseRow(row);
      table += '<tr>';
      cells.forEach(c => {
        table += `<td>${c}</td>`;
      });
      table += '</tr>';
    });
    table += '</tbody></table></div>';
    return table + '\n';
  });

  // Unordered lists
  html = html.replace(/(?:^[\t ]*[-*] .+$\n?)+/gm, match => {
    const items = match
      .trim()
      .split('\n')
      .map(line => {
        const content = line.replace(/^[\t ]*[-*] /, '');
        return `<li>${content}</li>`;
      });
    return `<ul>${items.join('')}</ul>\n`;
  });

  // Ordered lists
  html = html.replace(/(?:^[\t ]*\d+\. .+$\n?)+/gm, match => {
    const items = match
      .trim()
      .split('\n')
      .map(line => {
        const content = line.replace(/^[\t ]*\d+\. /, '');
        return `<li>${content}</li>`;
      });
    return `<ol>${items.join('')}</ol>\n`;
  });

  // Paragraphs
  html = html
    .split('\n\n')
    .map(block => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (/^<(h[1-6]|ul|ol|li|pre|hr|blockquote|table)/.test(trimmed)) return trimmed;
      return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');

  return DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'rel'] });
}

// ---------------------------------------------------------------------------
// Tiny reactive state helper
// ---------------------------------------------------------------------------

export function createState(initial) {
  let value = initial;
  const listeners = new Set();
  return {
    get: () => value,
    set(next) {
      value = typeof next === 'function' ? next(value) : next;
      listeners.forEach(fn => fn(value));
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

// ---------------------------------------------------------------------------
// Shared JSX sub-components
// ---------------------------------------------------------------------------

export function MessageBubble({ role, text, time }) {
  const isUser = role === 'user';
  const bubble = <div class="gaia-msg__bubble" />;
  if (isUser) {
    bubble.textContent = text;
  } else {
    bubble.innerHTML = renderMarkdown(stripCitations(text));
  }
  const timeStr = time ? new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div class={['gaia-msg', isUser ? 'gaia-msg--user' : 'gaia-msg--ai']}>
      {!isUser && <i class="gaia-msg__icon fas fa-robot" />}
      <div class="gaia-msg__content">
        {bubble}
        {timeStr && <span class="gaia-msg__time">{timeStr}</span>}
      </div>
    </div>
  );
}

export function ErrorBubble({ text, showRetry = true }) {
  return (
    <div class="gaia-msg gaia-msg--ai gaia-msg--error">
      <i class="gaia-msg__icon gaia-msg__icon--error fas fa-exclamation-circle" />
      <div class="gaia-msg__bubble gaia-msg__bubble--error">
        <span class="gaia-error__text">{text}</span>
        {showRetry && (
          <button class="gaia-retry-btn" data-tooltip="Resend message">
            <i class="fas fa-redo-alt" />
            <span>Retry</span>
          </button>
        )}
      </div>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div class="gaia-msg gaia-msg--ai gaia-typing" id="gaia-typing">
      <i class="gaia-msg__icon fas fa-robot" />
      <div class="gaia-msg__bubble">
        <span class="gaia-typing__dot" />
        <span class="gaia-typing__dot" />
        <span class="gaia-typing__dot" />
        <span class="gaia-typing__label">rAI is typing...</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure helper: process poll result from GAIA interaction
// ---------------------------------------------------------------------------

export function handlePollResult(result, startChatHistory) {
  const aiMessages = result.newAiMessages || [];
  const lastMsg = [...aiMessages].reverse().find(m => m.body?.text);
  const replyText = lastMsg?.body?.text || 'No response from rAI.';

  const baseHistory = result.chatHistory || startChatHistory || [];
  const isError = result.status === 'finished_with_error';
  const isContentFiltered = !isError && lastMsg?.msg_code?.toUpperCase() === 'API.ORC.EXECUTION.MODERATIONBLOCKED';

  let fullHistory;
  if (isContentFiltered) {
    const lastHumanIdx = baseHistory.findLastIndex(m => m.type === 'chat_message' && m.author?.role === 'human');
    fullHistory =
      lastHumanIdx >= 0
        ? [...baseHistory.slice(0, lastHumanIdx), ...baseHistory.slice(lastHumanIdx + 1)]
        : [...baseHistory];
  } else if (isError) {
    fullHistory = [...baseHistory];
  } else {
    fullHistory = [...baseHistory, ...aiMessages];
  }

  return { replyText, chatHistory: fullHistory.length > 0 ? fullHistory : null, isContentFiltered };
}

// ---------------------------------------------------------------------------
// Factory: create DOM-dependent chat helpers (scroll, messages, typing)
// ---------------------------------------------------------------------------

export function createChatHelpers(refs) {
  function scrollToBottom() {
    if (refs.messagesEl && (!refs.isVisible || refs.isVisible())) {
      requestAnimationFrame(() => {
        refs.messagesEl.scrollTo({ top: refs.messagesEl.scrollHeight, behavior: 'smooth' });
      });
    }
    updateScrollPill(true);
  }

  function updateScrollPill(forceHide = false) {
    if (!refs.scrollPillEl || !refs.messagesEl) return;
    if (forceHide) {
      refs.scrollPillEl.classList.remove('gaia-scroll-pill--visible');
      return;
    }
    const distFromBottom = refs.messagesEl.scrollHeight - refs.messagesEl.scrollTop - refs.messagesEl.clientHeight;
    if (distFromBottom > 120) {
      refs.scrollPillEl.classList.add('gaia-scroll-pill--visible');
    } else {
      refs.scrollPillEl.classList.remove('gaia-scroll-pill--visible');
    }
  }

  function hideEmptyState() {
    const el = refs.messagesEl?.querySelector('.gaia-empty-state');
    if (el) el.style.display = 'none';
  }

  function showEmptyState() {
    const el = refs.messagesEl?.querySelector('.gaia-empty-state');
    if (el) el.style.display = '';
  }

  function appendMessage(role, text, time) {
    hideEmptyState();
    const bubble = <MessageBubble role={role} text={text} time={time} />;
    refs.messagesEl.appendChild(bubble);
    scrollToBottom();
    return bubble;
  }

  function appendStreamingBubble() {
    hideEmptyState();
    const bubbleDiv = <div class="gaia-msg__bubble" />;
    const timeSpan = <span class="gaia-msg__time" />;
    timeSpan.style.display = 'none';
    const wrapper = (
      <div class={['gaia-msg', 'gaia-msg--ai']}>
        <i class="gaia-msg__icon fas fa-robot" />
        <div class="gaia-msg__content">
          {bubbleDiv}
          {timeSpan}
        </div>
      </div>
    );
    refs.messagesEl.appendChild(wrapper);

    const indicator =
      '<span class="gaia-streaming-indicator"><span></span><span></span><span></span><span></span><span></span><span class="gaia-streaming-label">rAI is typing...</span></span>';

    return function updateBubble(text, done = false) {
      const cleaned = stripCitations(text);
      bubbleDiv.innerHTML = renderMarkdown(cleaned) + (done ? '' : indicator);
      if (done) {
        timeSpan.textContent = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        timeSpan.style.display = '';
      }
      updateScrollPill();
    };
  }

  function showTyping() {
    if (refs.typingEl) refs.typingEl.style.display = 'flex';
    scrollToBottom();
  }

  function hideTyping() {
    if (refs.typingEl) refs.typingEl.style.display = 'none';
  }

  function setInputEnabled(enabled) {
    if (refs.inputEl) refs.inputEl.disabled = !enabled;
    if (refs.sendBtnEl) refs.sendBtnEl.disabled = !enabled;
  }

  return {
    scrollToBottom,
    updateScrollPill,
    hideEmptyState,
    showEmptyState,
    appendMessage,
    appendStreamingBubble,
    showTyping,
    hideTyping,
    setInputEnabled,
  };
}
